import { useRegisterSW } from 'virtual:pwa-register/vue';
import { useIntervalFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useNotificationStore } from '@/store/notificationStore';
import { NotificationClickEvent } from '@/models/common/Notification';
import { NotificationStates } from '@/models/enums/NotificationStates';
import { Guid } from 'guid-typescript';
import { appSettings } from '@/appSettings';
import type { Ref } from 'vue';

let needRefreshRef: Ref<boolean> | undefined;
let doUpdateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | undefined;
let isForceUpdate = false;

// SW build: verify silent update on login
async function fetchUpdatePolicy(): Promise<void> {
    try {
        const res = await fetch('/sw-update-policy.json', { cache: 'no-store' });
        if (res.ok) {
            const policy = await res.json();
            isForceUpdate = policy.forceUpdate === true;
            console.info('SW update policy:', { forceUpdate: isForceUpdate });
        }
    } catch {
        // Non-fatal — default to normal (non-forced) update
    }
}

/**
 * Check if a SW update is pending and activate it.
 * - Hotfix releases (forceUpdate): activate on any route change
 * - Normal releases: activate only on login transition (isLoginTransition = true)
 *
 * Returns true if an update was triggered (page will reload).
 */
export function activateIfUpdatePending(targetUrl?: string, isLoginTransition = false): boolean {
    if (!needRefreshRef?.value || !doUpdateServiceWorker) return false;

    if (!isForceUpdate && !isLoginTransition) return false;

    console.info('SW update pending — activating new service worker', { isForceUpdate, isLoginTransition, targetUrl });

    // Update the URL before the reload so the page reloads at the target route
    // (vite-plugin-pwa's controlling event calls window.location.reload())
    if (targetUrl) {
        history.replaceState(history.state, '', targetUrl);
    }
    void doUpdateServiceWorker().catch(() => {
        window.location.reload();
    });
    return true;
}

export function useServiceWorkerUpdate(): void {
    const notificationStore = useNotificationStore();
    const { notifications } = storeToRefs(notificationStore);

    let swRegistration: ServiceWorkerRegistration | undefined;

    function showUpdateNotification(): void {
        const message = 'notifications.newVersionAvailable';
        if (notifications.value?.some(c => c.message === message && c.state === NotificationStates.Info) === true) return;
        notificationStore.addByParams(Guid.create(), message, NotificationStates.Info, false, 'notifications.reloadClient', NotificationClickEvent.Reload, true);
    }

    const intervalMs = (appSettings.updateRefreshInterval || 30) * 1000;
    useIntervalFn(() => {
        if (swRegistration?.installing) return;
        if ('connection' in navigator && !navigator.onLine) return;
        swRegistration?.update().catch((e: unknown) => {
            console.warn('Service worker update failed:', e);
        });
    }, intervalMs);

    const { updateServiceWorker, needRefresh } = useRegisterSW({
        onRegistered(registration) {
            console.info('SW registered:', registration);
            if (registration != null) {
                swRegistration = registration;
            }
        },
        onNeedRefresh() {
            console.info('New content available — fetching update policy');
            // Fetch the update policy from the NEW build to determine if this is a hotfix.
            // Only hotfix releases show a notification; normal releases activate silently.
            void fetchUpdatePolicy().then(() => {
                if (isForceUpdate) {
                    showUpdateNotification();
                }
            });
        },
        onRegisterError(error: unknown) {
            console.warn('Service worker registration failed (non-fatal):', error);
        },
    });

    // Store refs at module level so activateIfUpdatePending() can access them
    needRefreshRef = needRefresh;
    doUpdateServiceWorker = updateServiceWorker;

    window.reloadApp = (): void => {
        const fallback = setTimeout(() => window.location.reload(), 5000);
        void updateServiceWorker()
            .then(() => clearTimeout(fallback))
            .catch(() => {
                clearTimeout(fallback);
                window.location.reload();
            });
    };
}
